<template>
  <div v-loading.fullscreen.lock="isLoading">
    <div class="row">
      <!-- Query conditioner -->
      <div class="col-xl-10">
        <el-card shadow="never">
          <div class="row">
            <div class="col-xl-6">
              <date-range-picker :from="queryParams.from" :to="queryParams.to" @on-change="applyDateRange"
                :shortcuts="dateRangeShortcuts" :earliest="dateRangePickerEarliest">
              </date-range-picker>
            </div>
            <div class="col-xl-3">
              <account-selector :user="user" :shop-enabled="false" @apply-account="applyAccount" @apply-tags="applyTags"></account-selector>
            </div>
            <div class="col-xl-3">
              <account-tag-input :user="user" @apply-tags="applyTags"></account-tag-input>
            </div>
          </div>
        </el-card>
      </div>

      <!-- More actions -->
      <div class="col-xl-2">
        <el-card shadow="never" style="height: 100%;" class="d-flex justify-content-center">
          <div v-if="userType === 'gaa'">
            <el-button type="primary" round 
              @click="openShopMap">
              <font-awesome-icon icon="map-marked-alt" color="white"/>&nbsp;
              {{ $t('Stat.Store_Map') }}
            </el-button><br><br>
          </div>
          <el-button type="danger" round icon="el-icon-download" v-if="userType !== 'eaa'"
            @click="exportShopsSummary">
            {{ $t('Stat.Business_Overview') }}
          </el-button>
        </el-card>
      </div>
    </div>

    <br>

    <!-- Main Figures -->
    <div class="row d-flex">
      <!-- Figures - Total amount against platform -->
      <div class="col-12 col-md-3">
        <div class="card card-stats card-raised">
          <div class="card-body">
            <div class="statistics">
              <div class="info">
                <div class="icon icon-primary">
                  <i class="now-ui-icons business_money-coins"></i>
                </div>
                <h2 class="info-title">
                  $<animated-number :value="stat.amount_all"></animated-number>
                </h2>
                <h5 class="stats-title">{{ $t('Stat.Revenue') }}</h5>
              </div>
            </div>
            <!-- INNER LIST OF PLATFORMS -->
            <div class="table-responsive pb-0">
              <n-table :data="tableOfAmounts" class="mb-0">
                <template slot-scope="{row}">
                  <td class="text-center">
                    <img :src="row.src" style="max-height: 35px; max-width: 84px;" v-if="row.src">
                    <span class="h5" v-if="row.text">{{ row.text }}</span>
                  </td>
                  <td class="text-right h4">$<animated-number :value="row.amount"></animated-number></td>
                  <td><!-- for spacing only --></td>
                </template>
              </n-table>
            </div>
          </div>
        </div>
      </div>

      <!-- Figures - Number of Orders against platform -->
      <div class="col-12 col-md-3">
        <div class="card card-stats card-raised">
          <div class="card-body">
            <div class="statistics">
              <div class="info">
                <div class="icon icon-primary">
                  <i class="now-ui-icons files_paper" style="color:#3F51B5"></i>
                </div>
                <h2 class="info-title">
                  <animated-number :value="stat.count_all"></animated-number>
                </h2>
                <h5 class="stats-title">{{ $t('Stat.Number_Of_Orders') }}</h5>
              </div>
            </div>
              
            <!-- INNER LIST OF PLATFORMS -->
            <div class="table-responsive pb-0">
              <n-table :data="tableOfCounts" class="mb-0">
                <template slot-scope="{row}">
                  <td class="text-center">
                    <img :src="row.src" style="max-height: 35px; max-width: 84px;" v-if="row.src">
                    <span class="h5" v-if="row.text">{{ row.text }}</span>
                  </td>
                  <td class="text-right h4"><animated-number :value="row.count"></animated-number></td>
                  <td><!-- for spacing only --></td>
                </template>
              </n-table>
            </div>
          </div>
        </div>
      </div>

      <!-- Figures - Online payments & other stats -->
      <div class="col-12 col-md-6">
        <div class="card card-stats card-raised">
          <div class="card-body">
            <div class="row">
              <div class="col-12 col-md-4">
                <div class="statistics">
                  <div class="info">
                    <div class="icon icon-info">
                      <i class="now-ui-icons shopping_credit-card"></i>
                    </div>
                    <h4 class="info-title">
                      $<animated-number :value="stat.amount_online"></animated-number>
                    </h4>
                    <h6 class="stats-title">{{ $t('Stat.Online_Payment_Total_Amount') }}</h6>
                    <h4 class="info-title">
                      <animated-number :value="stat.count_online"></animated-number>
                    </h4>
                    <h6 class="stats-title">{{ $t('Stat.Online_Payment_Number_Of_Orders') }}</h6>
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-4">
                <div class="statistics">
                  <div class="info">
                    <div class="icon icon-success">
                      <i class="now-ui-icons shopping_shop"></i>
                    </div>
                    <h4 class="info-title">{{shop_counts.count}}</h4>
                    <h6 class="stats-title">{{ $t('Stat.Number_Of_Stores') }}</h6>
                    <h4 class="info-title">{{shop_counts.new_shop}}</h4>
                    <h6 class="stats-title">{{ $t('Stat.Newly_Opened') }}</h6>
                    <h4 class="info-title">{{shop_counts.enabled}}</h4>
                    <h6 class="stats-title">{{ $t('Stat.Open_For_Business') }}</h6>
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-4">
                <div class="statistics">
                  <div class="info">
                    <div class="icon icon-warning">
                      <i class="now-ui-icons users_single-02"></i>
                    </div>
                    <h4 class="info-title">
                      <animated-number :value="stat.count_users"></animated-number>
                    </h4>
                    <h6 class="stats-title">{{ $t('Stat.Number_Of_Ordering_Members') }}</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pie charts -->
    <div class="row d-flex">
      <div class="col-md-3">
        <card class="card-chart" no-footer-line>
          <div class="chart-area">
            <pie-chart
              :chart-data="pie1"
              :height="220">
            </pie-chart>
          </div>
          <div slot="footer" class="stats d-flex justify-content-center">
            <h6 class="stats-title">{{$t('Stat.Various_Online_Payment_Methods') }}{{ $t('Stat.Proportion') }} ({{$t('Stat.Number_Of_Orders') }})</h6>
          </div>
        </card>
      </div>
      <div class="col-md-3">
        <card class="card-chart" no-footer-line>
          <div class="chart-area">
            <pie-chart
              :chart-data="pie2"
              :height="220">
            </pie-chart>
          </div>
          <div slot="footer" class="stats d-flex justify-content-center">
            <h6 class="stats-title">{{$t('Stat.Various_Online_Payment_Methods') }}{{ $t('Stat.Proportion') }} ({{$t('Stat.Amount') }})</h6>
          </div>
        </card>
      </div>
      <div class="col-md-3">
        <card class="card-chart" no-footer-line>
          <div class="chart-area">
            <pie-chart
              :chart-data="pie3"
              :height="220">
            </pie-chart>
          </div>
          <div slot="footer" class="stats d-flex justify-content-center">
            <h6 class="stats-title">{{ displayLabels.LABEL_MEAL_TYPES }}{{ $t('Stat.Proportion') }} ({{$t('Stat.Number_Of_Orders') }})</h6>
          </div>
        </card>
      </div>
      <div class="col-md-3">
        <card class="card-chart" no-footer-line>
          <div class="chart-area">
            <pie-chart
              :chart-data="pie4"
              :height="220">
            </pie-chart>
          </div>
          <div slot="footer" class="stats d-flex justify-content-center">
            <h6 class="stats-title">{{ displayLabels.LABEL_MEAL_TYPES }}{{ $t('Stat.Proportion') }} ({{$t('Stat.Amount') }})</h6>
          </div>
        </card>
      </div>
    </div>

    <!-- Trend charts -->
    <div class="row d-flex">

      <div class="col-md-3">
        <card class="card-chart" no-footer-line>
          <div class="chart-area">
            <trend-chart
              :chart-data="trend1"
              :options="chartOptions"
              :height="220">
            </trend-chart>
          </div>
          <div slot="footer" class="stats d-flex justify-content-center">
            <h6 class="stats-title">{{$t('Stat.Transaction_Type') }}{{ $t('Stat.Trend') }} ({{$t('Stat.Number_Of_Orders') }})</h6>
          </div>
        </card>
      </div>

      <div class="col-md-3">
        <card class="card-chart" no-footer-line>
          <div class="chart-area">
            <trend-chart
              :chart-data="trend2"
              :options="chartOptions"
              :height="220">
            </trend-chart>
          </div>
          <div slot="footer" class="stats d-flex justify-content-center">
            <h6 class="stats-title">{{$t('Stat.Transaction_Type') }}{{ $t('Stat.Trend') }} ({{$t('Stat.Amount') }})</h6>
          </div>
        </card>
      </div>

      <div class="col-md-3">
        <card class="card-chart" no-footer-line>
          <div class="chart-area">
            <trend-chart
              :chart-data="trend3"
              :options="chartOptions"
              :height="220">
            </trend-chart>
          </div>
          <div slot="footer" class="stats d-flex justify-content-center">
            <h6 class="stats-title">{{ displayLabels.LABEL_MEAL_TYPES }}{{ $t('Stat.Trend') }} ({{$t('Stat.Number_Of_Orders') }})</h6>
          </div>
        </card>
      </div>

      <div class="col-md-3">
        <card class="card-chart" no-footer-line>
          <div class="chart-area">
            <trend-chart
              :chart-data="trend4"
              :options="chartOptions"
              :height="220">
            </trend-chart>
          </div>
          <div slot="footer" class="stats d-flex justify-content-center">
            <h6 class="stats-title">{{ displayLabels.LABEL_MEAL_TYPES }}{{ $t('Stat.Trend') }} ({{$t('Stat.Amount') }})</h6>
          </div>
        </card>
      </div>
    </div>

    <!-- Expandable Lists of Top-10 shop rankings -->
    <el-row :gutter="8">

      <el-col :span="24" :lg="8">
        <el-divider><el-button type="text" @click="expansion.rankTotal = true">{{$t('Stat.View_Revenue_Ranking') }}</el-button></el-divider>
        <el-collapse-transition>
          <div v-show="expansion.rankTotal">
            <h5>{{ $t('Stat.Store_Ranking')}}：{{ $t('Stat.Revenue')}}</h5>
            <el-table :data="rankingTable.total">
              <el-table-column prop="name" :label="$t('Stat.Store')">
              </el-table-column>
              <el-table-column prop="total" label="＄" width="100" align="right">
                <div slot-scope="{row}">${{Number(row.total).toLocaleString()}}</div>
              </el-table-column>
            </el-table>
          </div>
        </el-collapse-transition>
      </el-col>

      <el-col :span="24" :lg="8">
        <el-divider><el-button type="text" @click="expansion.rankOrders = true">{{$t('Stat.View_Order_Ranking') }}</el-button></el-divider>
        <el-collapse-transition>
          <div v-show="expansion.rankOrders">
            <h5>{{ $t('Stat.Store_Ranking') }}：{{ $t('Stat.Number_Of_Orders') }}</h5>
            <el-table :data="rankingTable.order_count">
              <el-table-column prop="name" :label="$t('Stat.Store') ">
              </el-table-column>
              <el-table-column prop="order_count" :label="$t('Stat.Number_Of_Orders')" width="100" align="right">
              </el-table-column>
            </el-table>
          </div>
        </el-collapse-transition>
      </el-col>

      <el-col :span="24" :lg="8">
        <el-divider><el-button type="text" @click="expansion.rankUsers = true">{{$t('Stat.View_Member_Ranking') }}</el-button></el-divider>
        <el-collapse-transition>
          <div v-show="expansion.rankUsers">
            <h5>{{ $t('Stat.Store_Ranking')}}：{{ $t('Stat.Number_Of_Members')}}</h5>
            <el-table :data="rankingTable.user_count">
              <el-table-column prop="name" :label="$t('Stat.Store')">
              </el-table-column>
              <el-table-column prop="user_count" :label="$t('Stat.Number_Of_Members')" width="100" align="right">
              </el-table-column>
            </el-table>
          </div>
        </el-collapse-transition>
      </el-col>

    </el-row>

    <!-- Map drawer -->
    <el-drawer :title="$t('Stat.Store_Map')" :visible.sync="drawer.open"
      :destroy-on-close="true"
      direction="rtl" size="40%">
      <div id="shop-map-2" style="height: 100%;" v-loading="drawer.loading"></div>
    </el-drawer>

  </div>
</template>
<script>
import {
  AnimatedNumber,
  Table as NTable,
} from 'src/components';
import DateRangePicker from 'src/components/DateRangePicker';
import TrendChart from 'src/components/Charts/TrendChart';
import PieChart from 'src/components/Charts/PieChart';
import AccountSelector from './AccountSelector.vue';
import AccountTagInput from './AccountTagInput';

import Chart from 'chart.js';
import ChartJSPluginDatalabels from 'chartjs-plugin-datalabels';
//WARNING: We give datalabels anchoring settings by default, try not to change them~
Chart.defaults.global.maintainAspectRatio = false;
Chart.defaults.global.plugins.datalabels.anchor = 'end';//So that number label is attached to the "top" of each bar.
Chart.defaults.global.plugins.datalabels.font = {weight: 'bold'};
Chart.defaults.global.plugins.datalabels.formatter = function (val, ctx) {
  return val > 0? val: '';//Only show datalabels for non-zero values.
};
import GoogleMapsLoader from 'google-maps';
GoogleMapsLoader.KEY = process.env.VUE_APP_GOOGLE_MAP_API_KEY;
import MarkerClusterer from './MarkerClusterer';

import axios from 'axios';
import moment from 'moment';
import { setTimeout } from 'timers';
import { mapGetters } from 'vuex';

export default {
  components: {
    TrendChart, PieChart,
    ChartJSPluginDatalabels,
    AnimatedNumber,
    NTable,
    DateRangePicker,
    AccountSelector,
    AccountTagInput
  },

  mounted() {
    // Fetch statistics from server.
    this.getStats();
  },

  methods: {
    getStats() {
      this.isLoading = true;

      let from = Math.floor(this.queryParams.from.getTime() / 1000);
      let to = Math.floor(this.queryParams.to.getTime() / 1000);

      axios.get(
        `${process.env.VUE_APP_APIHOST}/${this.userType}/stat?from=${from}&to=${to}` +
        (this.queryParams.siId > 0? `&siId=${this.queryParams.siId}`: '') +
        (this.queryParams.eaaId > 0? `&eaaId=${this.queryParams.eaaId}`: '') + 
        (this.queryParams.tags.length? `&tags=${this.queryParams.tags.join(',')}`: '')
      )
      .then(response => {
        console.log('getStats', response.data);
        this.stat = response.data.stat;
        this.dates = response.data.dates
        this.shop_counts = response.data.shop_counts;
        this.drawPies();
        this.drawTrends(this.dates);
      })
      .catch(err => {
        console.log(`Cannot get stat`, err);
        let errMessage;
        if (err.data && err.data.code) {
          if (err.data.code == 'PROTOCOL_SEQUENCE_TIMEOUT')
            errMessage = this.$t('Stat.Message.Narrow_your_query');
          else
            errMessage = err.data.message
        }
        this.$notify.error({
          title: `${this.$t('Stat.Query_Error')} - ${this.$t('Stat.Statistical_Data')}`,
          message: errMessage
        });
        this.resetStat();
      })
      .finally(() => { 
        this.isLoading = false;
        //FIXME: Close up all ranking tables.
        this.expansion.rankTotal = false;
        this.expansion.rankOrders = false;
        this.expansion.rankUsers = false;
      });
    },
    getTop10(type) {
      let from = Math.floor(this.queryParams.from.getTime() / 1000);
      let to = Math.floor(this.queryParams.to.getTime() / 1000);

      axios.get(
        `${process.env.VUE_APP_APIHOST}/${this.userType}/stat/top-10?from=${from}&to=${to}&type=${type}` +
        (this.queryParams.siId > 0? `&siId=${this.queryParams.siId}`: '') +
        (this.queryParams.eaaId > 0? `&eaaId=${this.queryParams.eaaId}`: '') + 
        (this.queryParams.tags.length? `&tags=${this.queryParams.tags.join(',')}`: '')
      )
      .then(response => {
        // this.rank_order_count = response.data.rank_order_count.filter(r => {return r.order_count > 0;});
        // this.rank_total = response.data.rank_total.filter(r => {return r.total > 0;});
        // this.rank_user_count = response.data.rank_user_count.filter(r => {return r.user_count > 0;});

        this.rankingTable[type] = response.data.filter(r => { return r[type] > 0; });
      })
      .catch(err => {
        console.log(`Cannot get stat`, err);
        let errMessage;
        if (err.data && err.data.code) {
          if (err.data.code == 'PROTOCOL_SEQUENCE_TIMEOUT')
            errMessage = this.$t('Stat.Message.Narrow_your_query');
          else
            errMessage = err.data.message
        }
        this.$notify.error({
          title: `${this.$t('Stat.Query_Error')} - ${this.$t('Stat.Message.Up_to_one_month_for_export')}`,
          message: errMessage
        });
        this.resetStat();
      })
      .finally(() => {

      });
    },

    applyDateRange(dates) {
      this.queryParams.from = dates[0];
      this.queryParams.to = dates[1];
      this.getStats() //This is a promise.
    },
    applyAccount(si, eaa) {
      console.log(`[ORDERS] applyAccount: SI ${si.id} / EAA ${eaa.id}`);
      this.queryParams.siId = si.id || 0;
      this.queryParams.eaaId = eaa.id || 0;
      // this.queryParams.shopId = shopId;
      this.getStats();
    },
    applyTags(tags) {
      this.queryParams.tags = tags;
      this.getStats();
    },

    drawPies() {
      const stat = this.stat
      this.pie1 = {
        labels: ['Apple Pay', 'Google Pay', 'Line Pay', this.displayLabels.LABEL_PAYMENT_TYPE[0], this.displayLabels.LABEL_PAYMENT_TYPE[1]],
        datasets: [
          {
            data: [stat.count_apple, stat.count_google, stat.count_line, stat.count_direct, stat.count_online_others],
            backgroundColor: ['silver', 'tomato', 'lime', 'gold', '#BC8F8F']
          }
        ]
      };
      this.pie2 = {
        labels: ['Apple Pay', 'Google Pay', 'Line Pay', this.displayLabels.LABEL_PAYMENT_TYPE[0], this.displayLabels.LABEL_PAYMENT_TYPE[1]],
        datasets: [
          {
            data: [stat.amount_apple, stat.amount_google, stat.amount_line, stat.amount_direct, stat.amount_online_others],
            backgroundColor: ['silver', 'tomato', 'lime', 'gold', '#BC8F8F']
          }
        ]
      };
      this.pie3 = {
        labels: [this.displayLabels.LABEL_EAT_IN, this.displayLabels.LABEL_TO_GO, this.displayLabels.LABEL_TAKEAWAY, this.displayLabels.LABEL_DELIVERY],
        datasets: [
          {
            data: [stat.count_eat_in, stat.count_to_go, stat.count_takeaway, stat.count_delivery],
            backgroundColor: ['#80bfff', '#ffec80', '#8fce00', '#ff80c3']
          }
        ]
      };
      this.pie4 = {
        labels: [this.displayLabels.LABEL_EAT_IN, this.displayLabels.LABEL_TO_GO, this.displayLabels.LABEL_TAKEAWAY, this.displayLabels.LABEL_DELIVERY],
        datasets: [
          {
            data: [stat.amount_eat_in, stat.amount_to_go, stat.amount_takeaway, stat.amount_delivery],
            backgroundColor: ['#80bfff', '#ffec80', '#8fce00','#ff80c3']
          }
        ]
      };
    },

    drawTrends(dates) {
      //NOTE: You must assign the entire object rather than one prop after another.
      // Otherwise the chart won't update.
      this.trend1 = {
        labels: _pluck(dates, 'date'),
        datasets: [
          {
            label: this.$t('Stat.On_Site_Payment'), backgroundColor: '#afcecf', stack: 'stack1',
            data: _pluck(dates, 'cash_order_count')
          },
          {
            label: this.$t('Stat.Online_Payment'), backgroundColor: '#ffd280', stack: 'stack1',
            data: _pluck(dates, 'online_order_count')
          }
        ]
      };
      this.trend2 = {
        labels: _pluck(dates, 'date'),
        datasets: [
          {
            label: this.$t('Stat.On_Site_Payment'), backgroundColor: '#afcecf', stack: 'stack1',
            data: _pluck(dates, 'cash_amount')
          },
          {
            label: this.$t('Stat.Online_Payment'), backgroundColor: '#ffd280', stack: 'stack1',
            data: _pluck(dates, 'online_amount')
          }
        ]
      };
      this.trend3 = {
        labels: _pluck(dates, 'date'),
        datasets: [
          {
            label: this.displayLabels.LABEL_EAT_IN, backgroundColor: '#80bfff', stack: 'stack1',
            data: _pluck(dates, 'eat_in_order_count')
          },
          {
            label: this.displayLabels.LABEL_TO_GO, backgroundColor: '#ffec80', stack: 'stack1',
            data: _pluck(dates, 'to_go_order_count')
          },
          {
            label: this.displayLabels.LABEL_TAKEAWAY, backgroundColor: '#8fce00', stack: 'stack1',
            data: _pluck(dates, 'takeaway_order_count')
          },
          {
            label: this.displayLabels.LABEL_DELIVERY, backgroundColor: '#ff80c3', stack: 'stack1',
            data: _pluck(dates, 'delivery_order_count')
          }
        ]
      };
      this.trend4 = {
        labels: _pluck(dates, 'date'),
        datasets: [
          {
            label: this.displayLabels.LABEL_EAT_IN, backgroundColor: '#80bfff', stack: 'stack1',
            data: _pluck(dates, 'eat_in_amount')
          },
          {
            label: this.displayLabels.LABEL_TO_GO, backgroundColor: '#ffec80', stack: 'stack1',
            data: _pluck(dates, 'to_go_amount')
          },
          {
            label: this.displayLabels.LABEL_TAKEAWAY, backgroundColor: '#8fce00', stack: 'stack1',
            data: _pluck(dates, 'takeaway_amount')
          },
          {
            label: this.displayLabels.LABEL_DELIVERY, backgroundColor: '#ff80c3', stack: 'stack1',
            data: _pluck(dates, 'delivery_amount')
          }
        ]
      };
    },

    resetStat() {
      this.stat = {
        amount_all: 0,
        amount_cash: 0, amount_online: 0,
        amount_eat_in: 0, amount_to_go: 0, amount_delivery: 0,
        amount_qc: 0, amount_ue: 0, amount_fp: 0, amount_gf: 0, amount_co: 0,
        count_all: 0,
        count_cash: 0, count_online: 0,
        count_eat_in: 0, count_to_go: 0, count_delivery: 0,
        count_qc: 0, count_ue: 0, count_fp: 0, count_gf: 0, count_co: 0,
        count_users: 0
      };
      this.drawPies();
      this.drawTrends([]);
    },

    openShopMap() {
      this.drawer.loading = true;
      this.drawer.open = true;

      setTimeout(() => {
        GoogleMapsLoader.load(google => {

          // Fetch locations.
          axios.get(
            `${process.env.VUE_APP_APIHOST}/${this.userType}/locations?foo=bar` +
            (this.queryParams.siId > 0? `&siId=${this.queryParams.siId}`: '') +
            (this.queryParams.eaaId > 0? `&eaaId=${this.queryParams.eaaId}`: '')
          )
          .then(response => {
            let locations = response.data;

            // Setup map.
            const myLatlng = new window.google.maps.LatLng(23.979, 120.90);
            const mapOptions = {
              zoom: 8,
              center: myLatlng,
              scrollwheel: true
            };
            const map = new window.google.maps.Map(
              document.getElementById('shop-map-2'),
              mapOptions
            );

            // Setup markers and clusters.
            let markers = locations.map(function(location, index) {
              return new google.maps.Marker({
                // map: map,
                position: {
                  lng: location['longitude'], lat: location['latitude'],
                },
                label: location['shop_name']
              })
            });
            let markerClusterer = new MarkerClusterer(map, markers,
              {imagePath: `${process.env.BASE_URL}img/markerclusterer/m`}
            );

            this.drawer.loading = false;
          });

        });
      }, 1500);
      
    },

    exportShopsSummary() {
      let from = moment(this.queryParams.from).startOf('day');
      let to = moment(this.queryParams.to).endOf('day');
      if (to.diff(from, 'days') > 31) {
        alert(`${this.$t('Stat.Message.Up_to_one_month_for_export')}`);
        return;
      }
      window.open(
        `${process.env.VUE_APP_APIHOST}/${this.userType}/stat/export`
        + `?from=${from.unix()}&to=${to.unix()}`
      );
    },

    test() {
      console.log('divider clicked');
    }
  },

  data() {
    return {
      isLoading: false,
      queryParams: {
        from: moment().startOf('day').toDate(),
        to: new Date(),

        siId: 0, eaaId: 0, shopId: -1,
        tags: [],
      },

      stat: {
        amount_all: 0,
        amount_cash: 0, amount_online: 0,
        amount_eat_in: 0, amount_to_go: 0, amount_delivery: 0,
        amount_qc: 0, amount_ue: 0, amount_fp: 0, amount_gf: 0, amount_co: 0,
        count_all: 0,
        count_cash: 0, count_online: 0,
        count_eat_in: 0, count_to_go: 0, count_delivery: 0,
        count_qc: 0, count_ue: 0, count_fp: 0, count_gf: 0, count_co: 0,
        count_users: 0
      },
      dates: {},
      shop_counts: {count: 0, new_shop: 0, enabled: 0},
      // rank_order_count: [], rank_total: [], rank_user_count: [],
      pie1: {labels: [], datasets: [{data: []}]},
      pie2: {labels: [], datasets: [{data: []}]},
      pie3: {labels: [], datasets: [{data: []}]},
      pie4: {labels: [], datasets: [{data: []}]},
      trend1: {labels: [], datasets: [{data: []}]},
      trend2: {labels: [], datasets: [{data: []}]},
      trend3: {labels: [], datasets: [{data: []}]},
      trend4: {labels: [], datasets: [{data: []}]},
      chartOptions: {
        // legend: {display: false},
        layout: {padding: {left: 24, right: 24}},
        scales: {
          xAxes: [{barPercentage: 1, categoryPercentage: 0.8, stacked: true}],
          yAxes: [
            {ticks: {min: 0, precision: 0}}
          ]
        }
      },
      rankingTable: {
        total: [], order_count:[], user_count: []
      },

      drawer: {
        open: false,
        loading: false
      },
      expansion: {
        rankTotal: false,
        rankOrders: false,
        rankUsers: false
      },
    };
  },
  computed: {
    ...mapGetters([
      'appLang',
    ]),
    displayLabels() {
      let labels = {}

      // Adjust wordings accordingly.
      if (this.$route.meta.retail === true) {
        labels = {
          LABEL_EAT_IN: this.$t('Stat.Self_Pickup'),
          LABEL_TO_GO: this.$t('Stat.Customization'),
          LABEL_TAKEAWAY: this.$t('Stat.Takeaway'),
          LABEL_DELIVERY: this.$t('Stat.Home_Delivery'),
          LABEL_MEAL_TYPES: this.$t('Stat.Pickup_Method'),
          LABEL_PAYMENT_TYPE: [this.$t('Stat.Credit_Card'), this.$t('Stat.Other')]
        };
      }
      else {
        labels = {
          LABEL_EAT_IN: this.$t('Stat.Dine_In'),
          LABEL_TO_GO: this.$t('Stat.Self_Pickup'),
          LABEL_TAKEAWAY: this.$t('Stat.Takeaway'),
          LABEL_DELIVERY: this.$t('Stat.Delivery'),
          LABEL_MEAL_TYPES: this.$t('Stat.Meal_Pickup_Method'),
          LABEL_PAYMENT_TYPE: [this.$t('Stat.Credit_Card'), this.$t('Stat.Other')]
        };
      }

      return labels
    },
    dateRangeShortcuts() {
      return [
        {
          label: this.$t("Stat.Today"),
          from: moment()
            .startOf("day")
            .toDate()
        },
        {
          label: this.$t("Stat.Yesterday"),
          from: moment()
            .add(-1, "days")
            .startOf("day")
            .toDate(),
          to: moment()
            .add(-1, "days")
            .endOf("day")
            .toDate()
        },
        {
          label: this.$t("Stat.This_Week"),
          from: moment()
            .startOf("isoWeek")
            .toDate()
        },
        {
          label: this.$t("Stat.Last_Week"),
          from: moment()
            .add(-1, "week")
            .startOf("isoWeek")
            .toDate(),
          to: moment()
            .add(-1, "week")
            .endOf("isoWeek")
            .toDate()
        },
        {
          label: this.$t("Stat.This_Month"),
          from: moment()
            .startOf("month")
            .toDate()
        },
        {
          label: this.$t("Stat.Last_Month"),
          from: moment()
            .add(-1, "month")
            .startOf("month")
            .toDate(),
          to: moment()
            .add(-1, "month")
            .endOf("month")
            .toDate()
        }
      ]
    },
    user() {
      return this.$store.getters.user;
    },
    userType() {
      return this.$store.getters.userType;
    },

    dateRangePickerEarliest() {
      if ([ 'gaa', 'si' ].indexOf(this.userType) >= 0)
        return 0;
      return moment().add(-3, 'months').startOf('day').unix();
    },

    vanillaPlatformLogoSrc() {
      return `${process.env.BASE_URL}${process.env.VUE_APP_POSLOGO}`;
    },

    tableOfAmounts() {
      return [
        {
          platform: 'qc',
          src: `${process.env.BASE_URL}${process.env.VUE_APP_POSLOGO}`,
          amount: this.stat.amount_qc
        },
        {
          platform: 'ue',
          src: `${process.env.BASE_URL}img/icons/ubereats.svg`,
          amount: this.stat.amount_ue
        },
        {
          platform: 'fp',
          src: `${process.env.BASE_URL}img/icons/foodpanda.svg`,
          amount: this.stat.amount_fp
        },
        {
          platform: 'gf',
          src: `${process.env.BASE_URL}img/brand/gofoodie.png`,
          amount: this.stat.amount_gf
        },
        {
          platform: 'co',
          src: `${process.env.BASE_URL}img/icons/counter.svg`,
          // text: '櫃檯點餐',
          amount: this.stat.amount_co
        }
      ];
    },
    tableOfCounts() {
      return [
        {
          platform: 'qc',
          src: `${process.env.BASE_URL}${process.env.VUE_APP_POSLOGO}`,
          count: this.stat.count_qc
        },
        {
          platform: 'ue',
          src: `${process.env.BASE_URL}img/icons/ubereats.svg`,
          count: this.stat.count_ue
        },
        {
          platform: 'fp',
          src: `${process.env.BASE_URL}img/icons/foodpanda.svg`,
          count: this.stat.count_fp
        },
        {
          platform: 'gf',
          src: `${process.env.BASE_URL}img/brand/gofoodie.png`,
          count: this.stat.count_gf
        },
        {
          platform: 'co',
          src: `${process.env.BASE_URL}img/icons/counter.svg`,
          // text: '櫃檯點餐',
          count: this.stat.count_co
        }
      ];
    }
  },

  watch: {
    appLang: {
      handler(newVal, oldVal) {
        if (!newVal) return;
        // 重新繪製
        this.drawPies();
        this.drawTrends(this.dates)
      },
    },
    'expansion.rankTotal': function(v) {
      if (v) this.getTop10('total');
    },
    'expansion.rankOrders': function(v) {
      if (v) this.getTop10('order_count');
    },
    'expansion.rankUsers': function(v) {
      if (v) this.getTop10('user_count');
    }
  }
};

function _pluck(arr, key) {
  let ret = [];
  for (let i=0; i<arr.length; i++) 
    ret.push(arr[i][key]);
  return ret;
}
</script>
<style scoped>
.row > .col,
.row > [class*="col-"] {
  padding-left: 5px;
  padding-right: 5px;
}

.el-loading-mask {
  background: rgb(0,0,0,0.8) !important;
}
</style>
